import React from 'react';
import { Helmet } from 'react-helmet';
import styled, { css } from "styled-components";
import { motion } from "framer-motion";

// Глобальные стили для предотвращения горизонтального скроллинга
const GlobalStyles = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

// Стиль для секции
const Section = styled(motion.section)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f4f4f4;
  color: #333;
  transition: background 0.5s ease;
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Заголовок
const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 2rem;
  color: #007aff;
  animation: fadeIn 1s ease-in-out;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

// Контейнер для тарифов
const TariffContainer = styled(motion.div)`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 600px;
  margin: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
  @media (max-width: 768px) {
    width: 100%;
    padding: 1.5rem;
  }
`;

// Подзаголовок
const Subtitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: #333;
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
  }
`;

// Описание тарифа
const TariffDescription = styled.p`
  font-size: 1rem;
  color: #555;
  margin-bottom: 1rem;
  line-height: 1.5;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

// Дополнительная информация
const AdditionalInfo = styled.p`
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.5rem;
  line-height: 1.5;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
`;

const Careers = () => {
  const vacancies = [
    {
      title: 'Монтажник систем безопасности',
      description: 'Ответственный за установку и настройку систем видеонаблюдения, охранной и пожарной сигнализации.',
      requirements: [
        'Опыт работы в сфере монтажа систем безопасности от 1 года',
        'Знание принципов работы видеонаблюдения и охранной сигнализации',
        'Внимательность к деталям, ответственность',
        'Готовность работать в команде и самостоятельно'
      ],
      contact: '+79175607997'
    },
    {
      title: 'Специалист по техническому обслуживанию',
      description: 'Ответственный за обслуживание и ремонт систем безопасности.',
      requirements: [
        'Опыт работы в сфере обслуживания систем безопасности от 1 года',
        'Знание принципов работы видеонаблюдения, охранной и пожарной сигнализации, систем контроля доступа',
        'Внимательность к деталям, ответственность',
        'Готовность работать в команде и самостоятельно'
      ],
      contact: '+79175607997'
    },
    {
      title: 'Продавец-консультант',
      description: 'Ответственный за продажу и консультацию клиентов по системам безопасности.',
      requirements: [
        'Опыт работы в сфере продаж от 1 года',
        'Знание систем безопасности',
        'Коммуникабельность, умение работать с людьми',
        'Готовность работать в команде и самостоятельно'
      ],
      contact: '+79175607997'
    }
  ];

  const benefits = [
    'Дружелюбный и понимающий коллектив',
    'Своевременные выплаты зарплаты',
    'Частые выплаты дополнительной премии к праздникам',
    'Оплата топлива для транспортного средства',
    'Оплата SIM-карты для связи'
  ];

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const tariffVariants = {
    hidden: { opacity: 0, y: 50, scale: 1 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
    hover: { scale: 1.02, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  return (
    <GlobalStyles>
      <Helmet>
        <title>Вакансии | Сонет-Дом</title>
        <meta name="description" content="Актуальные вакансии в компании Сонет-Дом. Присоединяйтесь к нашей команде!" />
        <meta name="keywords" content="вакансии, работа, система безопасности, Сонет-Дом" />
      </Helmet>
      <Section
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        bgColor="#f4f4f4"
        textColor="#333"
        style={{ marginTop: '60px' }} // Добавляем отступ сверху
      >
        <Title>Вакансии</Title>

        <TariffContainer
          variants={tariffVariants}
          initial="hidden"
          whileInView="visible"
          whileHover="hover"
          viewport={{ once: true }}
        >
          <Subtitle>Почему стоит работать у нас?</Subtitle>
          {benefits.map((benefit, index) => (
            <AdditionalInfo key={index}>{benefit}</AdditionalInfo>
          ))}
        </TariffContainer>

        {vacancies.map((vacancy, index) => (
          <TariffContainer
            key={index}
            variants={tariffVariants}
            initial="hidden"
            whileInView="visible"
            whileHover="hover"
            viewport={{ once: true }}
          >
            <Subtitle>{vacancy.title}</Subtitle>
            <TariffDescription><strong>Описание:</strong> {vacancy.description}</TariffDescription>
            <Subtitle>Требования:</Subtitle>
            {vacancy.requirements.map((requirement, reqIndex) => (
              <AdditionalInfo key={reqIndex}>{requirement}</AdditionalInfo>
            ))}
            <AdditionalInfo><strong>Контактный телефон:</strong> <a href={`tel:${vacancy.contact}`}>{vacancy.contact}</a></AdditionalInfo>
          </TariffContainer>
        ))}
      </Section>
    </GlobalStyles>
  );
};

export default Careers;